import React, { ReactNode, useEffect, useState } from "react";
import { authProvider } from "./provider";
import AUTH_CONFIG from "../../configs/authConfig";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";

type AuthStatus = "restoring" | "authenticated" | "signing-in" | "error";

/**
 * Universal auth gate component.
 *
 * Works with any auth provider (native, browser, electron, etc.).
 * Ensures authentication is complete before rendering children.
 *
 * Flow:
 * 1. On mount, attempts to restore any cached session
 * 2. If session exists → render children
 * 3. If no session → trigger login (provider reloads page after login)
 * 4. If error → show error message
 */
export function AuthGate({
	children,
	loadingComponent,
	errorComponent,
}: {
	children: ReactNode;
	loadingComponent?: ReactNode;
	errorComponent?: ReactNode;
}) {
	const [status, setStatus] = useState<AuthStatus>("restoring");

	const [errorMessage, setErrorMessage] = useState<string>("");

	useEffect(() => {
		let cancelled = false;

		authProvider
			.handleRedirectPromise()
			.then(() => {
				if (cancelled) {
					return;
				}
				// Try to get a token to see if we're authenticated
				// Use configured scope if available, otherwise fall back to User.Read
				const scopes = AUTH_CONFIG.scope
					? [AUTH_CONFIG.scope]
					: ["User.Read"];
				return authProvider.getToken(scopes);
			})
			.then((result) => {
				if (cancelled) {
					return;
				}
				if (result?.accessToken) {
					setStatus("authenticated");
				} else {
					// No cached session — hand off to the auth provider for interactive login.
					setStatus("signing-in");
					authProvider.login();
				}
			})
			.catch((error) => {
				if (!cancelled) {
					console.error("AuthGate error:", error);
					const message = error?.message || String(error) || "Unknown authentication error";
					setErrorMessage(message);
					setStatus("error");
				}
			});

		return () => {
			cancelled = true;
		};
	}, []);

	if (status === "authenticated") {
		return <>{children}</>;
	}
	if (status === "error") {
		return (
			<>
				{errorComponent || (
					<LoadingDialog
						message={`Authentication failed: ${errorMessage}. Please refresh and try again.`}
						isOpen={true}
					/>
				)}
			</>
		);
	}
	// "restoring" | "signing-in"
	return (
		<>
			{loadingComponent || <LoadingDialog message="Authenticating..." isOpen={true} />}
		</>
	);
}
